import type { Cleanup } from '../helpers/cleanup'

// Private types ---------------------------------------------------------------

type ServerConnection = 'connected' | 'reconnecting'
type ConnectionState = ServerConnection | 'offline'

type OfflineController = {
  readonly update: () => void
  readonly disconnected: () => void
  readonly reconnected: () => void
  readonly cleanup: Cleanup
}

type OfflineHookContext = {
  readonly el: HTMLElement
  // Phoenix owns this controller for the lifetime of the hook instance.
  offlineController?: OfflineController
}

// Private functions -----------------------------------------------------------

// Browser connectivity cannot confirm that the LiveView connection recovered.
const connectionState = (
  browserOnline: boolean,
  serverConnection: ServerConnection
): ConnectionState => (browserOnline ? serverConnection : 'offline')

// Keeps browser events and LiveView callbacks under one banner state owner.
const setupOfflineBanner = (element: HTMLElement): OfflineController => {
  let serverConnection: ServerConnection = 'connected'

  // Renders the current connection state after browser events or DOM patches.
  const update = (): void => {
    element.dataset.connectionState = connectionState(
      window.navigator.onLine,
      serverConnection
    )
  }

  // Records the lost server connection even while the internet is unavailable.
  const disconnected = (): void => {
    serverConnection = 'reconnecting'
    update()
  }

  // Clears the server warning only when Phoenix confirms a successful rejoin.
  const reconnected = (): void => {
    serverConnection = 'connected'
    update()
  }

  // Releases browser listeners and any visual effects derived from the banner.
  const cleanup = (): void => {
    window.removeEventListener('online', update)
    window.removeEventListener('offline', update)
    element.dataset.connectionState = 'connected'
  }

  window.addEventListener('online', update)
  window.addEventListener('offline', update)
  update()

  return { update, disconnected, reconnected, cleanup }
}

// Public hook -----------------------------------------------------------------

export const FacadeOffline = {
  // Starts each hook instance with its current browser and server connection.
  mounted(this: OfflineHookContext): void {
    this.offlineController?.cleanup()
    this.offlineController = setupOfflineBanner(this.el)
  },

  // Retains connection state while server-owned banner content is patched.
  updated(this: OfflineHookContext): void {
    this.offlineController?.update()
  },

  // Shows server reconnection feedback through Phoenix's connection lifecycle.
  disconnected(this: OfflineHookContext): void {
    this.offlineController?.disconnected()
  },

  // Restores the connected state after Phoenix rejoins the LiveView.
  reconnected(this: OfflineHookContext): void {
    this.offlineController?.reconnected()
  },

  // Removes resources before the hook's DOM is discarded.
  destroyed(this: OfflineHookContext): void {
    this.offlineController?.cleanup()
    this.offlineController = undefined
  }
}
